import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, Ban, CheckCircle2, Clock, CreditCard,
  Download, Loader2, ShieldCheck,
} from "lucide-react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { matchBand } from "@/lib/brandingOptions";
import { BandSlider, BandReadout, maxSubscribableCap } from "@/components/billing/BandSlider";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_CHIP = {
  trial: "bg-blue-50 border-blue-200 text-blue-700",
  active: "bg-teal-50 border-teal-200 text-teal-700",
  past_due: "bg-orange-50 border-orange-200 text-orange-700",
  suspended: "bg-red-50 border-red-200 text-red-700",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function BillingPage() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [count, setCount] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/billing/summary").then((r) => {
      setSummary(r.data);
      setCount((c) => (c == null ? Math.max(1, r.data.headcount || 1) : c));
    })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices)).catch(() => setInvoices([]));
  }, []);

  useEffect(() => {
    const sid = params.get("subscription_id");
    if (sid) {
      setConfirming(true);
      api.post("/billing/confirm", { subscription_id: sid })
        .then((r) => {
          setSummary(r.data);
          toast.success(t("billing.subscriptionActive"));
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
        .finally(() => {
          setConfirming(false);
          navigate("/billing", { replace: true });
        });
    } else if (params.get("cancelled")) {
      toast.info(t("billing.checkoutCancelled"));
      navigate("/billing", { replace: true });
    }
    load();
  }, []); // eslint-disable-line

  const hasSub = Boolean(summary?.billing?.subscription_id) && !summary?.billing?.cancel_at_period_end;

  const act = async (plan) => {
    setBusyPlan(plan);
    const body = { plan, return_base: window.location.origin };
    try {
      if (hasSub) {
        const { data } = await api.post("/billing/change-plan", body);
        if (data.approval_url) {
          window.location.href = data.approval_url;
          return;
        }
        setSummary(data.summary);
        toast.success(t("billing.planChanged", { plan }));
      } else {
        const { data } = await api.post("/billing/subscribe", body);
        window.location.href = data.approval_url;
        return;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyPlan(null);
    }
  };

  const cancel = async () => {
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(t("billing.cancelledUntil", { date: fmtDate(data.active_until) }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!summary) return <Skeleton className="h-96 w-full rounded-none" data-testid="billing-loading" />;

  const b = summary.billing || {};
  const gatewayOff = !summary.gateway?.configured || !summary.gateway?.connected;
  const bands = summary.plans || [];
  const selBand = count != null ? matchBand(bands, count) : null;
  const maxCap = maxSubscribableCap(bands);
  const isCurrent = selBand && selBand.id === summary.plan;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      {confirming && (
        <div className="bg-white border border-slate-200 p-4 flex items-center gap-3" data-testid="billing-confirming">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--brand-accent-ink)" }} />
          <p className="text-[13px] text-slate-600">{t("billing.confirming")}</p>
        </div>
      )}

      {gatewayOff && (
        <div className="bg-amber-50 border border-amber-200 px-5 py-3.5" data-testid="billing-gateway-offline">
          <p className="text-[13px] text-amber-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {t("billing.gatewayOffline")}
          </p>
        </div>
      )}

      {summary.status === "past_due" && (
        <div className="bg-red-50 border border-red-200 px-5 py-3.5" data-testid="billing-pastdue-banner">
          <p className="text-[13px] text-red-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{t("billing.pastDueBanner", { date: fmtDate(b.grace_ends_at) })}</span>
          </p>
        </div>
      )}

      {/* current plan / status */}
      <div className="bg-white border border-slate-200 p-6" data-testid="billing-status-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{t("billing.currentPlan")}</p>
            <p className="text-[22px] font-bold capitalize mt-1" style={{ color: "var(--brand-primary)" }}
              data-testid="billing-current-plan">
              {bands.find((p) => p.id === summary.plan)?.name || summary.plan}
            </p>
            <p className="text-[12px] text-slate-500 mt-1 font-mono" data-testid="billing-headcount">
              {summary.headcount}{summary.employee_cap ? ` / ${summary.employee_cap}` : ""} {t("billing.employeesSuffix")}
            </p>
          </div>
          <div className="text-right space-y-2">
            <span className={`inline-block text-[10px] font-mono uppercase px-2 py-1 border ${STATUS_CHIP[summary.status] || "bg-slate-100 border-slate-200 text-slate-500"}`}
              data-testid="billing-status-chip">
              {summary.status}{b.comped ? " · comped" : ""}
            </span>
            {summary.trial && (
              <p className="text-[12px] text-slate-500 flex items-center justify-end gap-1.5" data-testid="billing-trial-countdown">
                <Clock className="h-3.5 w-3.5" /> {t("billing.trialEndsIn")} <b>{t("billing.trialEndsDays", { count: summary.trial.days_left })}</b> ({fmtDate(summary.trial.ends_at)})
              </p>
            )}
            {b.next_billing_time && !b.cancel_at_period_end && (
              <p className="text-[12px] text-slate-500" data-testid="billing-next-date">
                {t("billing.nextBilling")} <b>{fmtDate(b.next_billing_time)}</b>
              </p>
            )}
            {b.cancel_at_period_end && (
              <p className="text-[12px] text-orange-700" data-testid="billing-cancel-notice">
                {t("billing.cancelledAccessUntil")} <b>{fmtDate(b.period_end)}</b>
              </p>
            )}
          </div>
        </div>
        {b.subscription_id && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] font-mono text-slate-400" data-testid="billing-subscription-id">
              PayPal subscription {b.subscription_id}{b.payer_masked ? ` · payer ${b.payer_masked}` : ""}
            </p>
            {!b.cancel_at_period_end && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="billing-cancel-btn"
                    className="rounded-none h-8 text-[11px] border-red-300 text-red-700 hover:bg-red-50">
                    <Ban className="h-3.5 w-3.5" /> {t("billing.cancelSubscription")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("billing.cancelTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("billing.cancelDesc", { date: fmtDate(b.next_billing_time) })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none" data-testid="billing-cancel-abort">{t("billing.keepSubscription")}</AlertDialogCancel>
                    <AlertDialogAction className="rounded-none bg-red-600 hover:bg-red-700"
                      onClick={cancel} data-testid="billing-cancel-confirm">
                      {t("billing.cancelAtPeriodEnd")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      {/* headcount band picker */}
      <div>
        <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] flex items-center gap-2"
          style={{ color: "var(--brand-primary)" }}>
          <CreditCard className="h-4 w-4" style={{ color: "var(--brand-accent-ink)" }} /> {t("billing.plans")}
        </h2>
        <div className="bg-white border border-slate-200 p-6 mt-4" data-testid="billing-band-picker">
          <BandSlider bands={bands} count={count ?? 1} onChange={setCount} testid="billing-band-slider" />
          <div className="mt-8">
            <BandReadout band={selBand} maxCap={maxCap} testid="billing-band-readout">
              {selBand && !selBand.contact && !isCurrent && (
                <Button disabled={busyPlan !== null} onClick={() => act(selBand.id)}
                  data-testid="billing-band-cta"
                  className="rounded-none mt-5 brand-accent-bg border-0">
                  {busyPlan === selBand.id
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <ArrowRight className="h-4 w-4" />}
                  {hasSub ? t("billing.switchTo", { plan: selBand.name }) : t("billing.subscribe")}
                </Button>
              )}
              {isCurrent && (
                <div className="mt-5 h-9 px-4 inline-flex items-center border border-slate-200 text-[12px] text-slate-400"
                  data-testid="billing-band-current">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" style={{ color: "var(--brand-accent-ink)" }} /> {t("billing.yourPlan")}
                </div>
              )}
              {selBand?.contact && (
                <p className="mt-4 text-[12px] text-slate-500" data-testid="billing-band-contact-note">
                  {t("billing.contactProvider")}
                </p>
              )}
            </BandReadout>
          </div>
        </div>

        {/* all bands table */}
        <div className="bg-white border border-slate-200 mt-4 overflow-x-auto" data-testid="billing-bands-table">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {[t("bands.colBand"), t("bands.colUpTo"), t("bands.colMonthlyUsd"), t("bands.colPerEmployee"), ""].map((h, i) => (
                  <th key={i} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bands.map((p) => {
                const current = p.id === summary.plan;
                const active = p.id === selBand?.id;
                return (
                  <tr key={p.id} data-testid={`billing-plan-${p.id}`}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    style={active ? {
                      boxShadow: "inset 3px 0 0 var(--brand-accent)",
                      background: "color-mix(in srgb, var(--brand-accent) 7%, white)",
                    } : {}}
                    onClick={() => setCount(p.contact ? maxCap + 25 : p.employee_cap)}>
                    <td className={`px-4 py-2.5 ${active || current ? "font-bold" : "font-medium"}`}
                      style={{ color: "var(--brand-primary)" }}>
                      {p.name}
                      {current && (
                        <span className="ms-2 text-[9px] font-mono uppercase px-1.5 py-0.5 border align-middle"
                          style={{ borderColor: "var(--brand-accent)", color: "var(--brand-accent-ink)" }}
                          data-testid={`billing-plan-${p.id}-current`}>
                          {t("bands.current")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-500 whitespace-nowrap">
                      {p.contact ? `${maxCap}+` : p.employee_cap}
                    </td>
                    <td className="px-4 py-2.5 font-mono whitespace-nowrap">
                      {p.contact ? t("bands.contactUs") : `$${p.usd.toLocaleString()}`}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-400 whitespace-nowrap">
                      {p.contact ? "—" : `≈ $${p.per_employee_usd}`}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!p.contact && !current && (
                        <button disabled={busyPlan !== null}
                          onClick={(e) => { e.stopPropagation(); act(p.id); }}
                          data-testid={`billing-plan-${p.id}-subscribe`}
                          className="text-[11px] font-semibold hover:underline disabled:opacity-50"
                          style={{ color: "var(--brand-accent-ink)" }}>
                          {busyPlan === p.id ? "…" : hasSub ? t("billing.switchTo", { plan: p.name }) : t("billing.subscribe")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t("billing.pricesNoteUsd")}
        </p>
      </div>

      {/* invoices */}
      <div className="bg-white border border-slate-200" data-testid="billing-invoices">
        <div className="px-5 py-3.5 border-b border-slate-200">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.14em]" style={{ color: "var(--brand-primary)" }}>
            {t("billing.invoicesTitle")}
          </h2>
        </div>
        {!invoices || invoices.length === 0 ? (
          <p className="text-[12px] text-slate-400 px-5 py-6" data-testid="billing-invoices-empty">
            {t("billing.noPayments")}
          </p>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {[t("billing.colDate"), t("billing.colAmount"), t("billing.colTransaction"), t("billing.colStatus"), ""].map((h) => (
                  <th key={h} className="px-5 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <tr key={inv.id} data-testid={`billing-invoice-${inv.id}`}>
                  <td className="px-5 py-2.5 font-mono">{fmtDate(inv.date)}</td>
                  <td className="px-5 py-2.5 font-mono font-semibold">{inv.currency} {inv.amount}</td>
                  <td className="px-5 py-2.5 font-mono text-slate-400">{inv.txn_id || "—"}</td>
                  <td className="px-5 py-2.5">
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-teal-50 border-teal-200 text-teal-700">
                      paid{inv.simulated ? " · simulated" : ""}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    <button data-testid={`billing-receipt-${inv.id}`}
                      onClick={() => downloadFile(`/billing/invoices/${inv.id}/receipt.pdf`, `receipt-${inv.id.slice(0, 8)}.pdf`)}
                      className="inline-flex items-center gap-1 text-[11px] hover:underline"
                      style={{ color: "var(--brand-accent-ink)" }}>
                      <Download className="h-3.5 w-3.5" /> {t("billing.receipt")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
